import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import SignaturePad from '../ui/SignaturePad';
import { Camera, Plus, Trash2, ArrowDownLeft, ArrowUpRight, FileText, PenLine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DateText } from '../ui/week-badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  direction: 'in', date: new Date().toISOString().slice(0, 10),
  party_name: '', party_role: '', condition: '', notes: '', signature_image: null,
  our_signature_image: null,
  party_customer_id: '', project_id: '', site_location: '',
  responsible_employee_id: '', responsible_signature_image: null,
};

/**
 * Proof of condition when kit changes hands. "Received" is the customer's
 * drill or the hire forklift arriving on site; "handed back" closes it off
 * and stamps the asset as returned. Both sides sign, and recording the
 * customer and project is what lets us hold one of that project's crew
 * responsible for the tool afterwards.
 */
const AssetHandovers = ({ asset, isAdmin, conditions = [], customers = [], projects = [], employees = [], onRefresh }) => {
  const { t, i18n } = useTranslation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadFor, setUploadFor] = useState(null);
  const [signFor, setSignFor] = useState(null); // { record, side }
  const [pendingSig, setPendingSig] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/assets/${asset.id}/handovers`);
      setRecords(r.data);
    } catch { /* an empty list is a fine fallback here */ }
    setLoading(false);
  }, [asset.id]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error(t('inv.handover.dateRequired')); return; }
    // A hand-over with nobody's name on it settles no argument later.
    if (!form.party_name.trim()) { toast.error(t('inv.handover.nameRequired')); return; }
    setSaving(true);
    try {
      const customer = customers.find((c) => c.id === form.party_customer_id);
      const project = projects.find((p) => p.id === form.project_id);
      const member = (project?.team_members || []).find((m) => m.employee_id === form.responsible_employee_id);
      const responsible = employees.find((e) => e.id === form.responsible_employee_id);
      await axios.post(`${API}/inventory/assets/${asset.id}/handovers`, {
        ...form,
        party_name: form.party_name || null,
        party_role: form.party_role || null,
        party_customer_id: form.party_customer_id || null,
        party_customer_name: customer?.name || null,
        project_id: form.project_id || null,
        project_name: project?.name || null,
        site_location: form.site_location || null,
        responsible_employee_id: form.responsible_employee_id || null,
        responsible_employee_name: responsible?.name || null,
        responsible_employee_role: member?.role || null,
        condition: form.condition || null,
        notes: form.notes || null,
      });
      toast.success(t(form.direction === 'in' ? 'inv.handover.receiptRecorded' : 'inv.handover.handbackRecorded'));
      setOpen(false);
      setForm(EMPTY);
      load();
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
    setSaving(false);
  };

  /** The second signature, added once the other person is in front of you. */
  const saveSignature = async () => {
    if (!signFor || !pendingSig) return;
    try {
      await axios.put(`${API}/inventory/assets/${asset.id}/handovers/${signFor.record.id}/signature`,
        { side: signFor.side, signature_image: pendingSig });
      toast.success(t('inv.handover.signatureAdded'));
      setSignFor(null); setPendingSig(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.saveFailed')); }
  };

  /** The receipt must go through axios — a bare link carries no token. */
  const openReceipt = async (record) => {
    try {
      const lang = i18n.language?.startsWith('pl') ? 'pl' : 'en';
      const r = await axios.get(
        `${API}/inventory/assets/${asset.id}/handovers/${record.id}/pdf?lang=${lang}`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.handover.receiptFailed')); }
  };

  // A project's own crew — nobody else can sensibly take the tool on.
  const projectForSite = projects.find((p) => p.id === form.project_id);
  const siteFromProject = projectForSite
    ? [projectForSite.site_address, projectForSite.site_city].filter(Boolean).join(', ')
    : '';
  // Only that project's crew can be put down as responsible. An empty team
  // falls back to everyone rather than blocking the hand-over on site.
  const teamIds = new Set((projectForSite?.team_members || []).map((m) => m.employee_id));
  const responsibleOptions = teamIds.size > 0
    ? employees.filter((e) => teamIds.has(e.id))
    : employees;

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/inventory/assets/${asset.id}/handovers/${id}`);
      toast.success(t('inv.handover.deleted'));
      load();
      onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.deleteFailed')); }
  };

  const pickPhoto = (record) => { setUploadFor(record); fileRef.current?.click(); };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadFor) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/inventory/assets/${asset.id}/handovers/${uploadFor.id}/photo`, fd);
      toast.success(t('inv.handover.photoAttached'));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || t('inv.common.uploadFailed')); }
    setUploadFor(null);
  };

  return (
    <div className="space-y-3" data-testid="asset-handovers">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-semibold">{t('inv.handover.title', { count: records.length })}</h4>
          <p className="text-xs text-muted-foreground">{t('inv.handover.hint')}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid="add-handover-in-btn"
              onClick={() => { setForm({ ...EMPTY, direction: 'in' }); setOpen(true); }}>
              <ArrowDownLeft className="mr-1 h-4 w-4" /> {t('inv.handover.received')}
            </Button>
            <Button size="sm" data-testid="add-handover-out-btn"
              onClick={() => { setForm({ ...EMPTY, direction: 'out' }); setOpen(true); }}>
              <ArrowUpRight className="mr-1 h-4 w-4" /> {t('inv.handover.handedBack')}
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : records.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="no-handovers">
          {t('inv.handover.empty')}
        </p>
      ) : (
        <div className="space-y-2">
          {records.map((r) => (
            <div key={r.id} className="rounded-lg border p-3" data-testid={`handover-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={r.direction === 'in' ? 'secondary' : 'default'}>
                      {t(r.direction === 'in' ? 'inv.handover.received' : 'inv.handover.handedBack')}
                    </Badge>
                    <span className="text-sm font-medium"><DateText value={r.date} /></span>
                    {r.condition && <Badge variant="outline">{r.condition}</Badge>}
                  </div>
                  <p className="mt-1 text-sm">
                    {[r.party_name, r.party_role].filter(Boolean).join(' · ') || '—'}
                  </p>
                  {(r.party_customer_name || r.project_name || r.site_location) && (
                    <p className="mt-0.5 text-xs text-muted-foreground break-words">
                      {[r.party_customer_name, r.project_name, r.site_location].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {r.responsible_employee_name && (
                    <p className="mt-0.5 text-xs" data-testid={`handover-responsible-${r.id}`}>
                      <span className="text-muted-foreground">{t('inv.handover.responsible')}: </span>
                      {[r.responsible_employee_name, r.responsible_employee_role].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {r.notes && <p className="mt-0.5 text-xs text-muted-foreground break-words">{r.notes}</p>}
                  {r.signed_by && <p className="mt-0.5 text-xs text-muted-foreground">{t('inv.handover.ourSide')}: {r.signed_by}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" title={t('inv.handover.printableReceipt')}
                    onClick={() => openReceipt(r)} data-testid={`handover-pdf-${r.id}`}>
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="icon" className="h-7 w-7" title={t('inv.handover.attachPhoto')}
                        onClick={() => pickPhoto(r)} data-testid={`handover-photo-${r.id}`}>
                        <Camera className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500"
                        onClick={() => remove(r.id)} data-testid={`delete-handover-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {(r.signature_image || r.our_signature_image || r.responsible_signature_image || (r.pictures || []).length > 0) && (
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  {r.signature_image && (
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">{t('inv.handover.theirSignature')}</p>
                      <img src={r.signature_image} alt="signature"
                        className="h-14 rounded border bg-white object-contain p-1" />
                    </div>
                  )}
                  {r.our_signature_image && (
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">{t('inv.handover.ourSignature')}</p>
                      <img src={r.our_signature_image} alt={t('auto.inv.ourSignature')}
                        className="h-14 rounded border bg-white object-contain p-1" />
                    </div>
                  )}
                  {r.responsible_signature_image && (
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">{t('inv.handover.responsible')}</p>
                      <img src={r.responsible_signature_image} alt={t('auto.inv.responsibleSignature')}
                        className="h-14 rounded border bg-white object-contain p-1" />
                    </div>
                  )}
                  {(r.pictures || []).map((gid) => (
                    <img key={gid} src={`${API}/inventory/assets/${asset.id}/files/${gid}`} alt="condition"
                      className="h-14 w-14 rounded border object-cover" />
                  ))}
                </div>
              )}
              {isAdmin && (!r.signature_image || !r.our_signature_image
                || (r.responsible_employee_name && !r.responsible_signature_image)) && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {!r.signature_image && (
                    <Button size="sm" variant="outline" data-testid={`sign-theirs-${r.id}`}
                      onClick={() => { setSignFor({ record: r, side: 'theirs' }); setPendingSig(null); }}>
                      <PenLine className="mr-1 h-3.5 w-3.5" /> {t('inv.handover.addTheirSignature')}
                    </Button>
                  )}
                  {!r.our_signature_image && (
                    <Button size="sm" variant="outline" data-testid={`sign-ours-${r.id}`}
                      onClick={() => { setSignFor({ record: r, side: 'ours' }); setPendingSig(null); }}>
                      <PenLine className="mr-1 h-3.5 w-3.5" /> {t('inv.handover.addOurSignature')}
                    </Button>
                  )}
                  {r.responsible_employee_name && !r.responsible_signature_image && (
                    <Button size="sm" variant="outline" data-testid={`sign-responsible-${r.id}`}
                      onClick={() => { setSignFor({ record: r, side: 'responsible' }); setPendingSig(null); }}>
                      <PenLine className="mr-1 h-3.5 w-3.5" />
                      {t('inv.handover.addResponsibleSignature', { name: r.responsible_employee_name })}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="handover-form-dialog">
          <DialogHeader>
            <DialogTitle>{t(form.direction === 'in' ? 'inv.handover.recordReceipt' : 'inv.handover.recordHandback')}</DialogTitle>
            <DialogDescription>{t(form.direction === 'in' ? 'inv.handover.receiptHint' : 'inv.handover.handbackHint')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">{t('inv.handover.direction')}</Label>
                <Select value={form.direction} onValueChange={(v) => set('direction', v)}>
                  <SelectTrigger data-testid="handover-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">{t('inv.handover.receivedByUs')}</SelectItem>
                    <SelectItem value="out">{t('inv.handover.handedBack')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('inv.common.date')} *</Label>
                <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)}
                  required data-testid="handover-date" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.handover.theirName')} *</Label>
                <Input value={form.party_name} onChange={(e) => set('party_name', e.target.value)}
                  required placeholder={t('auto.inv.jKowalski')} data-testid="handover-party-name" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.handover.theirRole')}</Label>
                <Input value={form.party_role} onChange={(e) => set('party_role', e.target.value)}
                  placeholder={t('inv.handover.rolePlaceholder')} data-testid="handover-party-role" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.handover.customer')}</Label>
                <Select value={form.party_customer_id || 'none'}
                  onValueChange={(v) => set('party_customer_id', v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="handover-customer"><SelectValue placeholder={t('inv.common.select')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('inv.handover.noCustomer')}</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('inv.handover.project')}</Label>
                <Select value={form.project_id || 'none'}
                  onValueChange={(v) => {
                    const id = v === 'none' ? '' : v;
                    const p = projects.find((x) => x.id === id);
                    setForm((f) => ({
                      ...f,
                      project_id: id,
                      // Pre-fill the site from the project, and the customer
                      // too when it hasn't been picked yet — on site nobody
                      // wants to type what the system already knows.
                      site_location: f.site_location || (p ? [p.site_address, p.site_city].filter(Boolean).join(', ') : ''),
                      party_customer_id: f.party_customer_id || p?.customer_id || '',
                    }));
                  }}>
                  <SelectTrigger data-testid="handover-project"><SelectValue placeholder={t('inv.common.select')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('inv.handover.noProject')}</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.project_number ? `${p.project_number} — ${p.name}` : p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">{t('inv.handover.site')}</Label>
                <Input value={form.site_location} onChange={(e) => set('site_location', e.target.value)}
                  placeholder={siteFromProject || t('inv.handover.sitePlaceholder')}
                  data-testid="handover-site" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">{t('inv.handover.responsible')}</Label>
                <Select value={form.responsible_employee_id || 'none'}
                  onValueChange={(v) => set('responsible_employee_id', v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="handover-responsible"><SelectValue placeholder={t('inv.common.select')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('inv.handover.noResponsible')}</SelectItem>
                    {responsibleOptions.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {projectForSite && teamIds.size > 0
                    ? t('inv.handover.responsibleTeamHint', { project: projectForSite.name, count: teamIds.size })
                    : t('inv.handover.responsibleHint')}
                </p>
              </div>
              <div>
                <Label className="text-xs">{t('inv.common.condition')}</Label>
                {conditions.length > 0 ? (
                  <Select value={form.condition} onValueChange={(v) => set('condition', v)}>
                    <SelectTrigger data-testid="handover-condition"><SelectValue placeholder={t('inv.common.select')} /></SelectTrigger>
                    <SelectContent>
                      {conditions.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.condition} onChange={(e) => set('condition', e.target.value)}
                    data-testid="handover-condition" />
                )}
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">{t('common.notes')}</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)}
                  placeholder={t('inv.handover.notesPlaceholder')}
                  data-testid="handover-notes" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">{t('inv.handover.theirSignature')}</Label>
                <SignaturePad value={form.signature_image} onChange={(v) => set('signature_image', v)}
                  height={110} testIdPrefix="handover-sig" />
              </div>
              <div>
                <Label className="text-xs">{t('inv.handover.ourSignature')}</Label>
                <SignaturePad value={form.our_signature_image} onChange={(v) => set('our_signature_image', v)}
                  height={110} testIdPrefix="handover-our-sig" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('inv.handover.signaturesHint')}</p>
            {form.responsible_employee_id && (
              <div>
                <Label className="text-xs">
                  {t('inv.handover.responsibleSignature', {
                    name: responsibleOptions.find((e) => e.id === form.responsible_employee_id)?.name || '',
                  })}
                </Label>
                <SignaturePad value={form.responsible_signature_image}
                  onChange={(v) => set('responsible_signature_image', v)}
                  height={110} testIdPrefix="handover-resp-sig" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={saving} data-testid="save-handover-btn">
                {saving ? t('inv.common.saving') : t('inv.handover.saveRecord')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!signFor} onOpenChange={(v) => { if (!v) { setSignFor(null); setPendingSig(null); } }}>
        <DialogContent className="max-w-md" data-testid="handover-signature-dialog">
          <DialogHeader>
            <DialogTitle>
              {signFor?.side === 'responsible'
                ? t('inv.handover.addResponsibleSignature', { name: signFor?.record?.responsible_employee_name || '' })
                : t(signFor?.side === 'ours' ? 'inv.handover.addOurSignature' : 'inv.handover.addTheirSignature')}
            </DialogTitle>
            <DialogDescription>{t('inv.handover.lateSignatureHint')}</DialogDescription>
          </DialogHeader>
          <SignaturePad value={pendingSig} onChange={setPendingSig} height={130} testIdPrefix="handover-late-sig" />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => { setSignFor(null); setPendingSig(null); }}>
              {t('common.cancel')}
            </Button>
            <Button type="button" disabled={!pendingSig} onClick={saveSignature} data-testid="save-late-signature-btn">
              {t('common.save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetHandovers;
